import React, { useCallback, useMemo } from 'react'
import { Control, Controller } from 'react-hook-form'
import { createEditor, Editor, Element, Node, Range, Transforms } from 'slate'
import { withReact, useSlate, Slate, Editable, ReactEditor } from 'slate-react'
import { withHistory } from 'slate-history'

import { FormData } from "./types";

import { RadioButtonGroup } from '_components/RadioButton'
import { renderElement } from '_utils/description'

import './EditItemDescription.sass'

interface EditItemDescriptionProps {
  control: Control<FormData>;
}

const BlockControls = () => {
  const editor = useSlate()

  let style: 'paragraph' | 'feature' | 'heading' | undefined = undefined;
  if (editor.selection) {
    const block = Node.parent(editor, Range.start(editor.selection).path)
    if (Element.isElement(block)) {
      style = block.type
    }
  }

  return <RadioButtonGroup<'paragraph' | 'feature' | 'heading'>
    className="block-controls"
    collection={[
      {name: "Heading", value: "heading"},
      {name: "Feature heading", value: "feature"},
      {name: "Normal", value: "paragraph"},
    ]}
    currentValue={style}
    onChange={s => {
      Transforms.setNodes(editor, { type: s }, { match: n => Editor.isBlock(editor, n) })
      ReactEditor.focus(editor)
    }}
  />
}

export const EditItemDescription: React.FC<EditItemDescriptionProps> = ({ control }) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  // Let each line break create a new paragraph.
  const { insertBreak } = editor
  editor.insertBreak = () => {
    const lineStart = editor.selection && (Range.start(editor.selection).offset === 0)

    insertBreak()

    // Don't change the new node's type if the paragraph was simply shifted down.
    if (lineStart === false) Transforms.setNodes(
      editor,
      { type: 'paragraph' },
      { match: n => Editor.isBlock(editor, n) }
    )
  }

  const onKeyDown = useCallback(event => {
    // Override default behaviour of the Enter key.
    if (event.key === 'Enter') {
      if (editor.selection) {
        const start = Range.start(editor.selection)
        const leaf = Node.leaf(editor, start.path)
        if (leaf.text === '') {
          // If the current block is empty, do nothing - don't create extra paragraphs.
          event.preventDefault()
          return
        }
        // If the previous character is a newline, replace it with a break.
        if (start.offset > 0 && leaf.text[start.offset - 1] === '\n') {
          Transforms.delete(editor, { at: start, unit: 'character', reverse: true })
          // Break is inserted when the event is allowed to propagate.
          return
        }
        // If the next character is a newline, also replace it with a break.
        if (start.offset < leaf.text.length && leaf.text[start.offset] === '\n') {
          Transforms.delete(editor, { at: start, unit: 'character' })
          return
        }
        // Otherwise, insert only a simple line break, but only for paragraphs
        const block = Node.parent(editor, start.path)
        if (Element.isElement(block) && block.type === 'paragraph') {
          editor.insertText('\n')
          event.preventDefault()
        }
      }
    }
  }, [editor])

  return <div className="section">
    <p className="section-name">Description</p>
    <div className="item-description-container">
        <Controller name="description" control={control} render={({ value, onChange}) =>
          <Slate editor={editor} value={value} onChange={onChange}>
            <BlockControls />
            <Editable className="item-description"
              renderElement={renderElement}
              onKeyDown={onKeyDown}
              placeholder="Enter the description here..."
            />
          </Slate>
        }/>
    </div>
  </div>
}
